import { TransformConvertorBase } from './transform.convertor';
import { PivotTransformation } from 'app/model/dataflow/pivottransformation';
import { Transformation } from 'app/model/dataflow/transformation';
import { DataFlowExpressionUtils } from '../../expression.util';
import { Constants } from 'app/shared/shared-constants';
import { DataFlowSchemaAdapterUtils } from '../../dataflow-schema-adapter-utils';
import { PivotPlainObject } from 'worker/app-workers/shared/plainObjects/pivot.plainObject';
import { ExpressionColumnPlainObject } from 'worker/app-workers/shared/plainObjects/expressionColumn.plainObject';
import { ExpressionBasedColumn } from 'app/model/dataflow/expression-based.column';
import { IResourceContext } from 'shell-models-lib';

export class PivotConvertor extends TransformConvertorBase {
    private get pivotTransform(): PivotTransformation {
        return this.transformation as PivotTransformation;
    }

    private get pivotObject(): PivotPlainObject {
        return this.transformObject as PivotPlainObject;
    }

    public async convert(contextData: IResourceContext): Promise<Transformation> {
        await super.convert(contextData);
        this.pivotObject.columns.forEach((column) => {
            if (column.classType === ExpressionColumnPlainObject.classType) {
                const pivotColumn = this.pivotTransform.addColumn();
                pivotColumn.column.name = (column as ExpressionColumnPlainObject).name;
                pivotColumn.dataFlowExpression.expression = DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(
                    (column as ExpressionColumnPlainObject).expression
                );
                (this.pivotTransform.expressionColumns[0] as ExpressionBasedColumn).nameOptional =
                    this.pivotTransform.expressionColumns.length === 1 ? true : false;
            }
        });
        this.pivotObject.groupByColumns.forEach((column) => {
            const groupByColumn = this.pivotTransform.addGroupByColumn();
            const unescapedColumnName = Constants.dataFlowEscapedNameValidation.test(column.name)
                ? DataFlowSchemaAdapterUtils.removeWrappingCharacters(column.name)
                : column.name;
            groupByColumn.column.name = column.expression
                ? unescapedColumnName
                : DataFlowSchemaAdapterUtils.extractColumnName(unescapedColumnName);
            groupByColumn.dataFlowExpression.expression = column.expression || column.name;
        });
        if (!!this.pivotObject.pivotKeyColumn) {
            this.pivotTransform.pivotKeyColumn.expression = this.pivotObject.pivotKeyColumn;
        }
        this.pivotObject.pivotKeyValues.forEach((keyValue) =>
            this.pivotTransform.pivotKeyValues.push(DataFlowExpressionUtils.extractStringConfigValue(keyValue))
        );
        this.pivotTransform.nullValue = this.pivotObject.nullValue;

        if (this.pivotObject.configs.has('lateral')) {
            this.pivotTransform.lateralColumnArrangement = this.pivotObject.configs.get('lateral') === 'true';
            this._handledConfigs.push('lateral');
        }

        if (this.pivotObject.configs.has('columnNaming')) {
            const columnNamingPatternRegex: RegExp = /^(.*)(\$N)(.*)(\$V)(.*)$/;
            const columnNamingPatternRegex2: RegExp = /^(.*)(\$V)(.*)(\$N)(.*)$/;
            const namePattern = DataFlowExpressionUtils.extractStringConfigValue(
                this.pivotObject.configs.get('columnNaming')
            );
            this._handledConfigs.push('columnNaming');
            let search;
            if (columnNamingPatternRegex.test(namePattern)) {
                search = columnNamingPatternRegex.exec(namePattern);
                this.pivotTransform.columnNamePattern = '{0}$N{1}$V{2}';
            } else {
                search = columnNamingPatternRegex2.exec(namePattern);
                this.pivotTransform.columnNamePattern = '{0}$V{1}$N{2}';
            }
            if (search.length === 6) {
                this.pivotTransform.prefix = search[1];
                this.pivotTransform.middle = search[3];
                this.pivotTransform.suffix = search[5];
            }
        }

        super.postConvert();
        return this.transformation;
    }
}
